import {
  Box,
  VStack,
  HStack,
  Text,
  Skeleton,
  useColorModeValue,
  SimpleGrid,
  Badge,
  Flex,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  Heading,
  IconButton,
} from "@chakra-ui/react";
import React from "react";
import ListOfRecentEmployees from "./ListOfRecentEmployees";
import AvatarComponent from "../avatar";
import { FiFilter, FiCalendar, FiClock, FiMapPin } from "react-icons/fi";

const RecentEmployeeTable = ({ employeeData = [], isLoading }) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const bgColor = useColorModeValue("white", "gray.800");

  if (isLoading) {
    return (
      <Box p={6}>
        <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={6}>
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <Box key={i} bg="white" p={6} borderRadius="16px" shadow="sm" border="1px solid" borderColor="gray.100">
              <HStack spacing={4} mb={4}>
                <Skeleton height="40px" width="40px" borderRadius="full" />
                <VStack spacing={2} align="start" flex={1}>
                  <Skeleton height="16px" width="60%" />
                  <Skeleton height="14px" width="40%" />
                </VStack>
              </HStack>
              <VStack spacing={2} align="start">
                <Skeleton height="14px" width="100%" />
                <Skeleton height="14px" width="80%" />
                <Skeleton height="14px" width="60%" />
              </VStack>
            </Box>
          ))}
        </SimpleGrid>
      </Box>
    );
  }

  if (!isLoading && !employeeData?.length) {
    return (
      <Box p={8} textAlign="center">
        <VStack spacing={4}>
          <Box
            p={4}
            borderRadius="50%"
            bg="gray.100"
            color="gray.500"
            fontSize="2xl"
          >
            📊
          </Box>
          <VStack spacing={2}>
            <Text fontSize="lg" fontWeight="600" color="gray.700">
              No Recent Entries
            </Text>
            <Text color="gray.500" fontSize="sm">
              {employeeData?.detail || "No entries found for the selected date"}
            </Text>
          </VStack>
        </VStack>
      </Box>
    );
  }

  return (
    <Box>
      {/* Table Header */}
      <Flex 
        justifyContent="space-between" 
        alignItems="center" 
        p={6} 
        borderBottom="1px solid" 
        borderColor="gray.200"
        bg={bgColor}
      >
        <VStack align="start" spacing={1}>
          <Heading size="md" color="gray.900" fontWeight="600">
            Recent Entries
          </Heading>
          <Text color="gray.600" fontSize="sm">
            {employeeData.length} entries found
          </Text>
        </VStack>
        <IconButton
          icon={<FiFilter />}
          variant="ghost"
          onClick={onOpen}
          aria-label="Filter entries"
          color="gray.600"
          _hover={{ bg: "gray.100" }}
        />
                </Flex>

      {/* Entry Cards */}
      <VStack spacing={4} p={6} align="stretch">
        {employeeData?.map((item, index) => (
          <EntryCard
            key={`${index}key`}
            message={item.message}
            image={item.image_data}
          />
        ))}
      </VStack>

      {/* Filter Modal */}
      <Modal isOpen={isOpen} onClose={onClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Filter Entries</ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            <Text color="gray.600">Filter options coming soon...</Text>
          </ModalBody>
        </ModalContent>
      </Modal>
    </Box>
  );
};

// Entry Card Component
const EntryCard = ({ message, image }) => {
  const getEntryTypeColor = (message) => {
    if (message?.toLowerCase().includes('arrived')) return 'green';
    if (message?.toLowerCase().includes('left') || message?.toLowerCase().includes('departed')) return 'red';
    return 'blue';
  };

  const getEntryType = (message) => {
    if (message?.toLowerCase().includes('arrived')) return 'Check-in';
    if (message?.toLowerCase().includes('left') || message?.toLowerCase().includes('departed')) return 'Check-out';
    return 'Activity';
  };

  const extractName = (message) => {
    if (!message) return 'Unknown';
    const parts = message.split(' ');
    return parts[0] || 'Unknown';
  };

  return (
    <Box
      bg="white"
      p={6}
      borderRadius="16px"
      shadow="sm"
      border="1px solid"
      borderColor="gray.200"
      _hover={{ shadow: "md" }}
      transition="all 0.2s"
    >
      <HStack spacing={4} align="start">
        {/* Avatar */}
        <Box>
          <AvatarComponent name={extractName(message)} image={image} />
        </Box>

        {/* Entry Details */}
        <VStack spacing={3} align="start" flex={1}>
          <HStack spacing={3} align="center" justify="space-between" w="full">
            <Text fontWeight="600" color="gray.900" fontSize="md">
              {extractName(message)}
            </Text>
            <Badge
              colorScheme={getEntryTypeColor(message)}
              variant="subtle"
              px={3}
              py={1}
              borderRadius="full"
              fontSize="sm"
            >
              {getEntryType(message)}
            </Badge>
          </HStack>

          <Text fontSize="sm" color="gray.700" lineHeight="1.5">
            {message}
          </Text>
        </VStack>
      </HStack>
    </Box>
  );
};



export default RecentEmployeeTable;
